import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Services.css";
import HeroBg from "../../assets/images/services/NETWORK SOLUTIONS.jpg";

const ManagedNetworkSolutions = () => {
  const navigate = useNavigate();

  const items = useMemo(
    () => [
      {
        icon: "📄",
        title: "Network Assessments",
        desc: "We take a deep dive into your network performance, identifying performance issues, dead spots, and other network problems that may hinder your ability to work effectively. We'll help you resolve them so you can get back to what matters most.",
      },
      {
        icon: "☁️",
        title: "Cisco Meraki Support",
        desc: "We enable your business with powerful Cisco Meraki tools that simplify the management of your network devices. Through centralized control, you can improve network performance management and your business's growth potential.",
      },
      {
        icon: "🔍",
        title: "24/7 Network Monitoring",
        desc: "Network issues don't occur within a set timetable. That's why we monitor your network performance metrics at all hours and address frustrating issues like poor data flow, outages, or packet loss before they can disrupt your day-to-day operations.",
      },
    ],
    []
  );

  /* ── Card stagger reveal ─────────────────────────── */
  const cardRefs = useRef([]);
  useEffect(() => {
    const els = cardRefs.current.filter(Boolean);
    if (!els.length) return;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in");
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.25 }
    );

    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  /* ── Performance blocks stagger ─────────────────── */
  const perfBlockRefs = useRef([]);
  useEffect(() => {
    const els = perfBlockRefs.current.filter(Boolean);
    if (!els.length) return;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in");
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  /* ── CTA reveal ──────────────────────────────────── */
  const ctaRef = useRef(null);
  const [ctaInView, setCtaInView] = useState(false);
  useEffect(() => {
    const el = ctaRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCtaInView(true);
          obs.unobserve(entry.target);
        }
      },
      { threshold: 0.25 }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  /* ── Cyber card reveal ───────────────────────────── */
  const cyberRef = useRef(null);
  const [cyberInView, setCyberInView] = useState(false);
  useEffect(() => {
    const el = cyberRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCyberInView(true);
          obs.unobserve(entry.target);
        }
      },
      { threshold: 0.25 }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div className="servicePage">

      {/* ── HERO ───────────────────────────────────────────────────── */}
      <section className="hero-section">
        <div className="hero-section__grid-bg" aria-hidden="true" />

        <div className="hero-left">
          <div className="dd-serviceLabel">
            <span className="live-dot" aria-hidden="true" />
            <span>Network Solutions</span>
          </div>

          <h1 className="hc-hero__title">
            Secure networks for<br />always-on performance.
          </h1>

          <p className="hc-hero__lead">
            Strengthen your organization with scalable Network Solutions that deliver always-on
            connectivity, proactive monitoring, and resilient performance across locations, devices,
            and cloud environments so downtime drops and productivity stays high.
          </p>

          <button className="btn-orange" type="button" onClick={() => navigate("/contact")}>
            <span>Meet Our Experts Today</span>
            <svg className="btn-arrow" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        <div className="hero-right">
          <div className="hero-image-container">
            <span className="scan-corner tl" aria-hidden="true" />
            <span className="scan-corner tr" aria-hidden="true" />
            <span className="scan-corner bl" aria-hidden="true" />
            <span className="scan-corner br" aria-hidden="true" />
            <img className="hero-img" src={HeroBg} alt="Network Solutions" />
          </div>
        </div>
      </section>

      {/* ── NETWORK PERFORMANCE (3-col text) ───────────────────────── */}
      <section className="mns-performance">
        <div className="mns-performance__container">
          <header className="mns-performance__header">
            <span className="section-eyebrow">
              <span className="live-dot" aria-hidden="true" />
              Why ALG Data Guard
            </span>
            <h2 className="mns-performance__title">
              Simplify Network Performance Management With ALG Data Guard
            </h2>
            <p className="mns-performance__sub">
              ALG Data Guard makes IT and network performance management as simple as possible for
              you, providing the resources you need to focus on your business growth.
            </p>
          </header>

          <div className="mns-performance__grid">
            {[
              {
                h: "Your One–Stop Shop for Everything IT",
                p: <>We're proud to be a single-source provider of everything your business needs to maintain a <span className="mns-accent">healthy IT environment</span>. Whether you're looking to improve application performance, <span className="mns-accent">eliminate unsecured network devices</span>, or <span className="mns-accent">move to the cloud</span>, we have you covered.</>,
              },
              {
                h: "Managed Network Solutions Nationwide",
                p: <>With offices in <span className="mns-accent">Toronto</span>, we provide managed network solutions to businesses throughout North America. Whether you need <span className="mns-accent">remote or onsite support</span>, we'll be there to ensure your network performance is conducive to your success.</>,
              },
              {
                h: "We Live By Our Core Values",
                p: <><span className="mns-accent">Guide. Connect. Protect.</span> That's our motto, and it means we take the time to provide strategic IT solutions that connect and protect your entire business. Our goal is to build a long-lasting partnership that helps your business grow with effortless scalability.</>,
              },
            ].map((block, i) => (
              <article
                key={block.h}
                className="mns-performance__block"
                ref={(el) => (perfBlockRefs.current[i] = el)}
              >
                <h3 className="mns-performance__h3">{block.h}</h3>
                <p className="mns-performance__p">{block.p}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICE CARDS ──────────────────────────────────────────── */}
      <section className="mns-cards">
        <div className="mns-cards__container">
          <header className="mns-cards__header">
            <span className="section-eyebrow section-eyebrow--center">
              <span className="live-dot" aria-hidden="true" />
              Our Services
            </span>
            <h2 className="mns-cards__title">
              Find a Managed Network Solution for Your Unique Business
            </h2>
            <p className="mns-cards__sub">
              At ALG Data Guard, we know that every business faces a unique set of IT challenges.
              We're committed to providing customized managed network solutions that improve your
              connectivity and help you do business better.
            </p>
          </header>

          <div className="mns-cards__grid">
            {items.map((it, idx) => (
              <article
                key={it.title}
                className="mns-card"
                ref={(el) => (cardRefs.current[idx] = el)}
                style={{ transitionDelay: `${idx * 160}ms` }}
              >
                <div className="mns-iconWrap" aria-hidden="true">
                  <span className="mns-icon">{it.icon}</span>
                </div>
                <h3 className="mns-card__title">{it.title}</h3>
                <p className="mns-card__text">{it.desc}</p>
                <button
                  className="mns-card__btn"
                  type="button"
                  onClick={() => navigate("/contact")}
                >
                  Learn More
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ─────────────────────────────────────────────── */}
      <section className="mns-cta">
        <div
          ref={ctaRef}
          className={`mns-cta__card${ctaInView ? " in" : ""}`}
        >
          <div className="mns-cta__left">
            <img
              className="mns-cta__illustration"
              src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1400&q=80"
              alt="Support illustration"
            />
          </div>

          <div className="mns-cta__right">
            <h2 className="mns-cta__heading">
              Empower Your Business With<br />
              <span>Managed Network Solutions</span>
            </h2>
            <p className="mns-cta__body">
              Unlock stronger performance and reliability with ALG Data Guard's managed network
              solutions. Reach out today to explore how our specialized IT support can help your
              business grow with confidence.
            </p>
            <button className="mns-cta__btn" type="button" onClick={() => navigate("/contact")}>
              Find My Network Solution
              <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* ── CYBERSECURITY CROSS-SELL ────────────────────────────────── */}
      <section className="mns-cyber">
        <div className="mns-cyber__inner">
          <article
            ref={cyberRef}
            className={`mns-cyber__card${cyberInView ? " in" : ""}`}
          >
            <div className="mns-cyber__iconWrap" aria-hidden="true">
              <svg className="mns-cyber__icon" viewBox="0 0 24 24" fill="none">
                <path
                  d="M7.5 10V8.2C7.5 5.9 9.4 4 11.7 4h.6C14.6 4 16.5 5.9 16.5 8.2V10"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M6.5 10h11c.6 0 1 .4 1 1v8c0 .6-.4 1-1 1h-11c-.6 0-1-.4-1-1v-8c0-.6.4-1 1-1Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 14v3"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>

            <h3 className="mns-cyber__title">Explore Our Cybersecurity Solutions</h3>

            <p className="mns-cyber__text">
              ALG Data Guard delivers a robust suite of security services to protect
              business‑critical data. From{" "}
              <span className="mns-cyber__accent">multi‑layered prevention</span> and{" "}
              <span className="mns-cyber__accent">incident response</span> to{" "}
              <span className="mns-cyber__accent">audits</span> and{" "}
              <span className="mns-cyber__accent">compliance consulting</span>, everything needed
              to reduce risk and stay ahead of threats is covered.
            </p>

            <button
              className="mns-cyber__btn"
              type="button"
              onClick={() => navigate("/services/Cybersecurity-Services")}
            >
              Find My Security Solution
              <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </article>
        </div>
      </section>

    </div>
  );
};

export default ManagedNetworkSolutions;